import { alpha, darken, lighten } from '@mui/material/styles';
import {
  ColorPrimitives,
  elementaryPrimitives,
  fontStacks,
} from './primitives';

export const buildColorTokens = ({
  colors,
  extendedColors,
  materialColors,
  translucentColors,
}: ColorPrimitives) => ({
  content: {
    primary: colors.black,
    secondary: colors.gray2,
    tertiary: colors.gray4,
    muted: colors.gray12,
    disabled: colors.gray11,
    placeholder: colors.gray7,
    subtle: colors.slate,
    faint: colors.gray14,
    inverse: colors.white,
    inverseMuted: colors.gray10,
    onAccent: {
      primary: translucentColors.black87,
      secondary: colors.white,
    },
    translucent: {
      primary: translucentColors.black87,
      secondary: translucentColors.black60,
      disabled: translucentColors.black38,
    },
  },
  surface: {
    base: colors.white,
    subtle: colors.gray3,
    muted: colors.gray10,
    tinted: colors.gray9,
    hover: colors.gray8,
    strong: colors.gray5,
    soft: colors.gray15,
    sunken: colors.gray6,
    emphasis: colors.gray1,
    control: colors.gray14,
    track: colors.gray,
    trackStrong: colors.gray4,
    light: colors.gray13,
    divider: colors.gray16,
    raised: colors.gray17,
    inverse: colors.black,
    inverseMuted: colors.gray2,
    selected: colors.lightBlue3,
    selectedSubtle: colors.lightBlue6,
    highlight: colors.lightBlue4,
    active: colors.lightBlue5,
    shade: extendedColors.neutral235,
    shadeLight: extendedColors.neutral240,
    fade: extendedColors.neutral254,
    infoSubtle: extendedColors.lightBlueAlice,
    scrollbar: extendedColors.scrollbarThumb,
    chrome: materialColors.grey[100],
    chromeHover: materialColors.grey[300],
    backdrop: translucentColors.ink50,
    translucent: {
      hover: translucentColors.black04,
      selected: translucentColors.black08,
      focus: translucentColors.black12,
      disabled: translucentColors.black12,
      pressed: colors.blackOpacity5,
      scrim: translucentColors.black40,
      scrimStrong: translucentColors.black50,
      frosted: translucentColors.white90,
      frostedLight: translucentColors.white60,
      accent: translucentColors.aliceBlueAt90,
      frostedGray: translucentColors.gray221At34,
      subtle: translucentColors.black05,
    },
  },
  border: {
    default: colors.gray5,
    subtle: colors.gray8,
    light: colors.gray13,
    muted: colors.gray,
    divider: colors.gray16,
    strong: colors.black,
    control: colors.gray7,
    controlStrong: colors.gray1,
    emphasis: colors.gray17,
    medium: colors.gray18,
    faint: colors.gray14,
    selected: colors.lightBlue3,
    contrast: colors.gray2,
    info: colors.lightBlue2,
    highlight: colors.lightBlue4,
    tinted: colors.gray9,
    dividerSubtle: colors.gray3,
    disabled: colors.gray10,
    inverse: colors.white,
    mediumSoft: colors.gray11,
    mediumStrong: colors.gray4,
    translucent: translucentColors.black12,
    translucentStrong: translucentColors.black23,
  },
  shadow: {
    faint: translucentColors.black03,
    light: translucentColors.black04,
    subtle: translucentColors.black05,
    soft: translucentColors.black08,
    medium: translucentColors.black10,
    strong: translucentColors.black12,
    heavy: translucentColors.black25,
    inkSubtle: colors.blackOpacity5,
    solid: colors.gray11,
    solidSoft: colors.gray14,
    solidMuted: colors.gray17,
    solidLight: colors.gray13,
  },
  icon: {
    primary: colors.black,
    secondary: colors.gray2,
    tertiary: colors.gray4,
    muted: colors.gray12,
    disabled: colors.gray11,
    subtle: colors.slate,
    faint: colors.gray14,
    inverse: colors.white,
    // The check mark shown in a tooltip once something is copied.
    copied: colors.green,
    translucent: {
      active: translucentColors.black54,
      disabled: translucentColors.black26,
    },
  },
  status: {
    error: {
      main: colors.red2,
      bright: colors.red,
      soft: colors.red100,
      bg: colors.red10,
      bgStrong: colors.lightRed,
      bgSoft: colors.lightRed2,
      fill: alpha(colors.red, 0.2),
      fillSoft: alpha(colors.red100, 0.2),
    },
    warning: {
      main: colors.orange,
      strong: colors.orange100,
      accent: colors.orange2,
      light: lighten(colors.orange, 0.2),
      bg: colors.orange10,
      bgStrong: colors.lightOrange,
      bgSoft: colors.lightOrange2,
      fill: alpha(colors.orange, 0.2),
      fillStrong: alpha(colors.orange100, 0.2),
    },
    success: {
      main: colors.green100,
      bright: colors.green,
      light: lighten(colors.green100, 0.2),
      bg: colors.green10,
      bgStrong: colors.green200,
      bgSoft: colors.lightTurqoise2,
      bgBright: colors.lightTurqoise,
      fill: alpha(colors.green, 0.2),
      fillStrong: alpha(colors.green100, 0.2),
    },
    info: {
      main: colors.blue,
      bg: colors.gray9,
      bgStrong: colors.lightBlue,
    },
    neutral: {
      fill: translucentColors.black20,
    },
    attention: {
      bg: colors.attentionYellow,
    },
  },
  accent: {
    primary: colors.blue4,
    primaryStrong: colors.blue6,
    secondary: colors.blue7,
    secondaryStrong: darken(colors.blue7, 0.3),
    secondaryFaint: alpha(colors.blue7, 0.1),
    link: colors.blue,
    selected: colors.blue5,
    ai: colors.magenta,
    aiSubtle: colors.lightMagenta,
    aiFaint: colors.magentaOpacity5,
    aiTint: colors.lightPink,
    aiGradient: {
      start: extendedColors.aiGradientPink,
      middle: extendedColors.aiGradientRose,
      end: extendedColors.aiGradientBlue,
      startFaint: alpha(extendedColors.aiGradientPink, 0.33),
      endFaint: alpha(extendedColors.aiGradientBlue, 0.33),
    },
    secondaryMuted: colors.blue2,
    tertiary: colors.purple,
    tertiarySubtle: colors.lightPurple,
  },
  chart: {
    status: {
      success: colors.green,
      successStrong: colors.green100,
      warning: colors.orange,
      danger: colors.red,
    },
    score: {
      low: colors.red100,
      lowStrong: colors.red2,
      medium: colors.orange100,
      high: colors.green100,
    },
    grid: colors.gray16,
    gridLine: colors.gray,
    idle: colors.gray4,
    marker: colors.black,
    segment: colors.gray12,
    segmentTrack: colors.gray5,
    other: extendedColors.slateGray,
    expectedArea: extendedColors.neutral238,
    delayArea: extendedColors.neutral248,
    axis: colors.gray18,
    neutral: colors.gray1,
    muted: colors.gray11,
    inactive: colors.jetGrey,
    highlight: colors.yellow,
    band: alpha(colors.gray18, 0.2),
    bandStrong: translucentColors.gray161At40,
    // Series colors by series count: row N - 1 colors a chart with N series,
    // and the last row is used past it.
    seriesSequences: [
      [
        extendedColors.seriesPlum,
        extendedColors.seriesRose,
        extendedColors.seriesRed,
        extendedColors.seriesOrange,
        extendedColors.seriesTeal,
        extendedColors.seriesTurquoise,
        extendedColors.seriesAqua,
        extendedColors.seriesGreen,
        extendedColors.seriesLime,
        extendedColors.seriesYellow,
        extendedColors.seriesBlue,
        extendedColors.seriesSky,
        extendedColors.seriesNavy,
        extendedColors.seriesViolet,
        extendedColors.seriesLavender,
      ].map((primary) => ({ primary, light: alpha(primary, 0.2) })),
    ],
  },
  identity: {
    // Picked by name, so the same person keeps the same color.
    avatars: [
      extendedColors.avatarPurple,
      extendedColors.avatarRed,
      extendedColors.avatarBlue,
      extendedColors.avatarGreen,
      extendedColors.avatarYellow,
      extendedColors.avatarOrange,
      extendedColors.avatarMagenta,
    ].map((background) => ({ background, text: colors.white })),
    github: extendedColors.githubInk,
    gitlab: extendedColors.gitlabOrange,
    bitbucket: extendedColors.bitbucketBlue,
    azure: extendedColors.azureBlue,
    azureFaint: alpha(extendedColors.azureBlue, 0.12),
  },
  code: {
    // Icons on the code editor and the chat code block.
    editorIcon: colors.white,
    editorBg: extendedColors.editorBg,
    gutterBg: extendedColors.editorGutterBg,
    gutterText: extendedColors.editorGutterText,
    selection: translucentColors.gray200At30,
    diffAddedBg: colors.lightGreen,
    diffRemovedBg: colors.lightRed,
  },
  neutral: materialColors.grey,
});

export type ColorTokens = ReturnType<typeof buildColorTokens>;

export const buildScaleTokens = (
  { shadow: shadowColor }: ColorTokens,
  { colors, translucentColors }: ColorPrimitives,
) => ({
  space: {
    hairline: '1px',
    xxs: '2px',
    xxsPlus: '3px',
    xs: '4px',
    xsPlus: '5px',
    smMinus: '6px',
    sm: '8px',
    smPlus: '9px',
    mdMinus: '10px',
    md: '12px',
    mdPlus: '14px',
    lg: '16px',
    lgPlus: '18px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
    hugeMinus: '38px',
    huge: '40px',
    massive: '48px',
  },
  radius: {
    xxs: '2px',
    xs: '3px',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    xxl: '16px',
    capsule: '100px',
    pill: '999px',
    round: '50%',
  },
  fontFamily: {
    sans: fontStacks.geist,
    sansFace: fontStacks.geistFace,
    mono: fontStacks.geistMono,
    monoSystem: 'monospace',
    code: "'SFMono-Regular', 'Menlo', 'Consolas', 'Liberation Mono', 'Courier New', monospace",
    display: 'DM Sans',
    brand: fontStacks.geist,
  },
  fontSize: {
    xxxs: '9px',
    xxs: '10px',
    xs: '11px',
    sm: '12px',
    md: '13px',
    base: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px',
    xxxl: '24px',
    display: '28px',
    displayLg: '34px',
    displayXl: '40px',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    none: '1',
    snug: '1.2',
    tight: '1.3',
    comfortable: '1.4',
    relaxed: '1.5',
    loose: '1.6',
    sm: '18px',
    md: '20px',
    display: '44px',
  },
  shadow: {
    xs: `0 1px 3px ${shadowColor.medium}`,
    sm: `0 2px 6px ${shadowColor.subtle}`,
    smSoft: `0 2px 6px ${shadowColor.soft}`,
    smMedium: `0 2px 6px ${shadowColor.medium}`,
    md: `0px 4px 8px ${shadowColor.medium}`,
    mdInk: `0 4px 8px 0 ${alpha(colors.black, 0.1)}`,
    mdTight: `0 2px 8px ${shadowColor.medium}`,
    lg: `0 4px 16px ${shadowColor.strong}`,
    sheet: `0px -6px 15px -5px ${shadowColor.faint}`,
    float: `0px 16px 250px 0px ${shadowColor.subtle}`,
    glow: `0px 0px 5px 0px ${shadowColor.heavy}`,
    halo: `0px 0px 18px 0px ${translucentColors.inkBlueAt13}`,
    haze: `0 0 11.5px 0 ${translucentColors.lavenderAt50}`,
  },
  motion: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },
});

export type ScaleTokens = ReturnType<typeof buildScaleTokens>;

export const colorTokens = buildColorTokens(elementaryPrimitives);

export const scaleTokens = buildScaleTokens(colorTokens, elementaryPrimitives);
